import axios from "axios";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "tsp_session_id";
const ADMIN_KEY = "tsp_admin_flag";
const REFERRER_SENT_KEY = "tsp_referrer_sent";

/** Stable anonymous session id, persisted across page loads. */
export const getSessionId = () => {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "anon";
  }
};

/** Read/persist the admin flag. URL `?admin=1` flips it on permanently for
 *  this browser; `?admin=0` clears it. Events from admin sessions are still
 *  captured but excluded from public analytics aggregations server-side. */
export const isAdminSession = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("admin")) {
      const val = params.get("admin");
      if (val === "0" || val === "false") {
        localStorage.removeItem(ADMIN_KEY);
      } else {
        localStorage.setItem(ADMIN_KEY, "1");
      }
    }
    return localStorage.getItem(ADMIN_KEY) === "1";
  } catch {
    return false;
  }
};

/** Fire an analytics event. Fails silently — never blocks the UI. */
export const trackEvent = (eventType, extra = {}) => {
  try {
    const isAdmin = isAdminSession();
    let referrer;
    try {
      // Only attach referrer on the first event of a session
      if (!sessionStorage.getItem(REFERRER_SENT_KEY)) {
        referrer = document.referrer || "";
        sessionStorage.setItem(REFERRER_SENT_KEY, "1");
      }
    } catch {
      /* ignore */
    }
    axios
      .post(`${API}/track`, {
        event_type: eventType,
        session_id: getSessionId(),
        path: window.location.pathname + window.location.search,
        metadata: extra,
        is_admin: isAdmin,
        referrer,
      })
      .catch(() => {});
  } catch {
    /* swallow */
  }
};

/** Hook: fires a pageview on every route change. Drop once at the app root. */
export const usePageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    // Do not track admin/dashboard routes.
    if (!location.pathname.startsWith("/admin")) {
      trackEvent("pageview");
    }
  }, [location.pathname, location.search]);
};

/** Hook: auto-track clicks on any element that has data-testid OR data-track.
 *  Lightweight — only one delegated listener on document. Captures the
 *  testid, the text label of the element, and the current path. */
export const useClickTracker = () => {
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    const handler = (e) => {
      const el = e.target && e.target.closest
        ? e.target.closest("[data-track], [data-testid]")
        : null;
      if (!el) return;
      const testid = el.getAttribute("data-testid") || "";
      const track = el.getAttribute("data-track") || "";
      // Ignore noisy form controls and the modal scrim itself
      const tag = (el.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      const label =
        (el.getAttribute("aria-label") ||
          (el.innerText || "").replace(/\s+/g, " ").trim().slice(0, 80)) ||
        "";
      trackEvent("click", {
        testid: testid || undefined,
        track: track || undefined,
        label,
        tag,
      });
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);
};

/** Hook: scroll milestones (25/50/75/100% of the document). Fires each
 *  milestone at most once per page. */
export const useScrollMilestoneTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    const fired = new Set();
    const compute = () => {
      const doc = document.documentElement;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.round(((window.scrollY || doc.scrollTop) / max) * 100);
      const milestones = [25, 50, 75, 100];
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          trackEvent(`scroll_${m}`, { path: location.pathname });
        }
      }
    };
    let pending = false;
    const onScroll = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        compute();
        pending = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also evaluate once on mount (in case the page is short)
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);
};

/** Hook: observe any element marked with data-track-section="<name>" and
 *  fire `section_view` the first time it crosses 50% visibility. */
export const useSectionImpressionTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    if (typeof IntersectionObserver === "undefined") return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const name = entry.target.getAttribute("data-track-section");
            if (name && !seen.has(name)) {
              seen.add(name);
              trackEvent("section_view", { section: name });
              observer.unobserve(entry.target);
            }
          }
        }
      },
      { threshold: [0.5] }
    );
    const attach = () => {
      document
        .querySelectorAll("[data-track-section]")
        .forEach((el) => observer.observe(el));
    };
    // First pass + observe later-added nodes
    attach();
    const mo = new MutationObserver(() => attach());
    mo.observe(document.body, { subtree: true, childList: true });
    return () => {
      observer.disconnect();
      mo.disconnect();
    };
  }, [location.pathname]);
};

/** Hook: fire `session_end` when the user closes/reloads the tab. */
export const useSessionEndTracker = () => {
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    const handler = () => {
      try {
        const data = JSON.stringify({
          event_type: "session_end",
          session_id: getSessionId(),
          path: window.location.pathname + window.location.search,
          metadata: {},
          is_admin: isAdminSession(),
        });
        // Use sendBeacon for reliability during unload
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: "application/json" });
          navigator.sendBeacon(`${API}/track`, blob);
        }
      } catch {
        /* swallow */
      }
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, []);
};
